import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { supabaseClient } from '@/lib/supabase/client'
import type { User, Session } from '@supabase/supabase-js'

interface AuthUser {
  id: string; username: string; display_name: string; avatar_url: string | null
  banner_url?: string | null; bio?: string | null; is_verified: boolean
  karma_score: number; role: string; email: string; follower_count?: number
  following_count?: number; post_count?: number; website?: string | null
}

interface AuthCtx {
  user: AuthUser | null; supabaseUser: User | null; session: Session | null
  loading: boolean; initialized: boolean
  signOut: () => Promise<void>; refreshUser: () => Promise<void>
}

const Ctx = createContext<AuthCtx>({
  user: null, supabaseUser: null, session: null, loading: true, initialized: false,
  signOut: async () => {}, refreshUser: async () => {}
})
export const useAuth = () => useContext(Ctx)

async function fetchProfile(uid: string): Promise<AuthUser | null> {
  try {
    const { data, error } = await supabaseClient
      .from('users')
      .select('id,username,display_name,avatar_url,banner_url,bio,website,is_verified,karma_score,role,email,follower_count,following_count,post_count')
      .eq('id', uid)
      .single()
    if (error) { console.error('fetchProfile error:', error); return null }
    return data as AuthUser | null
  } catch (e) { console.error('fetchProfile exception:', e); return null }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [initialized, setInitialized] = useState(false)
  const initDone = useRef(false)

  const refreshUser = async () => {
    try {
      const { data: { session: s } } = await supabaseClient.auth.getSession()
      if (s?.user) {
        setSupabaseUser(s.user); setSession(s)
        const p = await fetchProfile(s.user.id); setUser(p)
      } else { setUser(null); setSupabaseUser(null); setSession(null) }
    } catch (e) { console.error('refreshUser error:', e) }
  }

  useEffect(() => {
    supabaseClient.auth.getSession().then(async ({ data: { session: s } }) => {
      if (s?.user) {
        setSupabaseUser(s.user); setSession(s)
        const p = await fetchProfile(s.user.id); setUser(p)
      }
      setLoading(false); setInitialized(true); initDone.current = true
    }).catch(() => { setLoading(false); setInitialized(true); initDone.current = true })

    const { data: { subscription } } = supabaseClient.auth.onAuthStateChange(async (event, s) => {
      if (event === 'SIGNED_OUT') {
        setUser(null); setSupabaseUser(null); setSession(null)
      } else if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && s?.user) {
        setSupabaseUser(s.user); setSession(s)
        const p = await fetchProfile(s.user.id); setUser(p)
      } else if (event === 'TOKEN_REFRESHED' && s?.user) {
        setSession(s); setSupabaseUser(s.user)
      }
      if (!initDone.current) { setLoading(false); setInitialized(true); initDone.current = true }
    })
    return () => subscription.unsubscribe()
  }, [])

  const signOut = async () => {
    await supabaseClient.auth.signOut()
    setUser(null); setSupabaseUser(null); setSession(null)
  }

  return (
    <Ctx.Provider value={{ user, supabaseUser, session, loading, initialized, signOut, refreshUser }}>
      {children}
    </Ctx.Provider>
  )
}
